import { SerializedStyles } from '@emotion/react';
import React from 'react';

import BaseCardView from './BaseCardView';
import TextWithSymbols from './TextWithSymbols';

import { css } from '@emotion/react';
import { TributeDef } from '@mythos/game/GameModel';
import { getTributeLogic } from '@mythos/game/TributeLogic';

const SUBTITLES: Record<string, string> = {
  'Grow Bounty': 'Derion, God of Bounty',
  'Grow Conquest': 'Tyros, God of War',
  'Grow Faith': 'Zeona, God of Order',
  Overpower: 'Warhammer',
  'Seek Wonder': 'Temple on Mountain',
  'All-In': 'The Flame',
  'Show Might': 'The Chariot',
  'Impulsive Choice': 'The Hare',
  'Patient Choice': 'The Tortoise',
  'Show Piety': 'The Dancers',
  'Bring Peace': 'The Dove',
  'Grow Community': 'The Mother',
  'Grow Polis': 'The Fountain',
  'Grow Legend': 'The Hero',
  Persevere: 'The Dog',
  'Seek Modesty': 'The Mule',
  'Seek Humility': 'The Monk',
  'Punish Weakness': 'The Snake',
};

type TributeCardEventHandler<TCard> = (
  card: TCard,
  event: React.MouseEvent,
) => void;

type Props<TCard extends TributeDef = TributeDef> = {
  card: TCard;

  onCardEnter?: TributeCardEventHandler<TCard>;
  onCardLeave?: TributeCardEventHandler<TCard>;
  onClick?: TributeCardEventHandler<TCard>;

  overlayStyle?: React.CSSProperties;
  overlayCSS?: SerializedStyles;
  overlayChildren?: React.ReactNode;
} & (
  | {
      mini: true;
      currentAge: number;
    }
  | {
      mini?: false;
      currentAge?: number;
    }
);

const TributeCardView2 = React.forwardRef<HTMLDivElement, Props>(
  (props, ref) => {
    const { card, currentAge, mini } = props;
    const handleMouseEnter = (e: React.MouseEvent) => {
      props.onCardEnter && props.onCardEnter(card, e);
    };

    const handleMouseLeave = (e: React.MouseEvent) => {
      props.onCardLeave && props.onCardLeave(card, e);
    };

    const handleClick = (e: React.MouseEvent) => {
      props.onClick && props.onClick(card, e);
    };

    const tributeLogic = getTributeLogic(card);
    const thresholds = tributeLogic?.thresholds;
    const description = tributeLogic?.description();

    const card_style: React.CSSProperties = {
      backgroundColor: '#333',
      ...(card.imageName && {
        backgroundImage: `url(${card.imageName})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }),
    };

    return (
      <BaseCardView
        className={'tribute'}
        ref={ref}
        cardStyle={card_style}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onLeftClick={handleClick}
        overlayChildren={props.overlayChildren}
        overlayCSS={props.overlayCSS}
        overlayStyle={props.overlayStyle}
        landscape={true}
        mini={mini}
      >
        <div css={Styles.content}>
          <div css={[Styles.textBox, Styles.name]}>
            {card.name.toUpperCase()}
          </div>
          {SUBTITLES[card.name] && (
            <div css={[Styles.textBox, Styles.subtitle]}>
              {SUBTITLES[card.name]}
            </div>
          )}
          <div css={Styles.spacer} />
          {description && (
            <div css={[Styles.textBox, Styles.effect]}>
              <TextWithSymbols
                symbolStyle={Styles.symbol}
                content={description}
                perAgePillOptions={
                  thresholds
                    ? {
                        display: 'all-values',
                        values: thresholds,
                        currentAge,
                      }
                    : undefined
                }
              />
            </div>
          )}
        </div>
      </BaseCardView>
    );
  },
);

export default TributeCardView2;

const Styles = {
  content: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flexGrow: 1,
    padding: 1,
  }),
  textBox: css({
    alignSelf: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    borderRadius: 5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }),
  spacer: css({
    flexGrow: 1,
  }),
  name: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'rgb(211, 211, 211)',
    fontSize: 10,
    '.mini &': {
      fontSize: 9,
    },
    padding: '1px 14px',
    // fontFamily: 'Mythos-CardTitle',
    textBoxTrim: 'both',
    textBoxEdge: 'cap alphabetic',
  }),
  subtitle: css({
    color: 'rgb(180, 180, 180)',
    fontSize: 5,
    padding: '1px 8px 2px',
    fontStyle: 'italic',
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  }),
  effect: css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 7,
    color: 'rgb(211, 211, 211)',
    textAlign: 'center',
    padding: '1px 11px 2px',
    fontWeight: 400,
    lineHeight: 1.3,
  }),
  symbol: css({
    height: 10,
  }),
} as const;
